import { LoadingButton } from '@mui/lab';
import { Stack } from '@mui/material';
import {
  ReactElement,
  Suspense,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';

import {
  createNewTopic,
  getTopicSubscriptions,
  getUserChatTopicListWithFilter,
} from '@app/adapter/chat-service';
import { updateOrder } from '@app/adapter/order-service';
import { CancelModal } from '@app/components/Order/CancelModal';
import { ProductDetailInfo } from '@app/components/Product/ProductDetailInfo';
import { BottomAppBar } from '@app/components/Shared/BottomAppBar';
import { HeadBlock } from '@app/components/Shared/HeadBlock';
import { Loading } from '@app/components/Shared/Loading';
import { TopNavPortal } from '@app/components/TopNav/TopNavPortal';
import { userAuthInfoSelector } from '@app/domain/app';
import { getOrder } from '@app/domain/network-actions';
import { orderCountState } from '@app/domain/top-nav';
import { useSetSnackbar } from '@app/hooks/useSetSnackbar';
import { LoadableState } from '@app/types/common';
import { Order, OrderStatus, OrderUpdate } from '@app/types/order';
import { isError } from '@app/utils/error';
import { canOrderCancel } from '@app/utils/order';

export function Show(): ReactElement {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const setSnackbar = useSetSnackbar();
  const authInfo = useRecoilValue(userAuthInfoSelector);
  const [navBadgeCount, setNavBadgeCount] = useRecoilState(orderCountState);
  const [order, setOrder] = useState<Order>();
  const [isCancelModal, setIsCancelModal] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadable, setLoadable] = useState<LoadableState>(
    LoadableState.HAS_VALUE
  );

  const product = useMemo(() => {
    return typeof order?.lineItems[0].product === 'object'
      ? { ...order.lineItems[0].product, organization: order.organization }
      : null;
  }, [order]);

  const fetchOrder = useCallback(async () => {
    if (!authInfo || !orderId || loadable === LoadableState.LOADING) {
      return;
    }

    setLoadable(LoadableState.LOADING);
    try {
      const result = await getOrder(
        authInfo.accessToken,
        authInfo.userId,
        orderId
      );
      setOrder(result.data);
      setLoadable(LoadableState.HAS_VALUE);
    } catch (e) {
      if (isError(e)) {
        console.error(e.message);
      }
      setLoadable(LoadableState.HAS_ERROR);
      setSnackbar(true, '応募情報が取得できませんでした');
    }
  }, [authInfo, loadable, orderId, setSnackbar]);

  useEffect(() => {
    void fetchOrder();
    // eslint-disable-next-line
  }, []);

  const updateOrderToCancel = useCallback(
    async (organizationId: string, orderId: string, payload: OrderUpdate) => {
      if (!authInfo || !order) return;

      setIsLoading(true);
      try {
        const result = await updateOrder(
          authInfo.accessToken,
          authInfo.fingerPrint,
          organizationId,
          orderId,
          payload
        );
        setOrder(result.data);
        setSnackbar(true, '辞退しました', 'success');
        setIsCancelModal(false);
        if (order.status === OrderStatus.PENDING) {
          setNavBadgeCount(navBadgeCount - 1);
        }
      } catch (e) {
        if (isError(e)) {
          console.error(e.message);
        }
        setSnackbar(true, '辞退できませんでした');
      } finally {
        setIsLoading(false);
      }
    },
    [authInfo, navBadgeCount, order, setSnackbar, setNavBadgeCount]
  );

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const organizationName =
    typeof order?.organization === 'object' && order?.organization?.name
      ? order.organization.name
      : 'Unknown';

  const organizationOwnerId =
    typeof order?.organization === 'object' &&
    order?.organization?.customFields.ownerUserId
      ? order.organization.customFields.ownerUserId
      : 'Unknown';

  const handleClickOpenChat = async () => {
    if (!authInfo) return;

    setIsLoading(true);
    try {
      const response = await getUserChatTopicListWithFilter(
        authInfo.accessToken,
        authInfo.fingerPrint,
        authInfo.userId,
        { expand: 'user' }
      );
      const topics = response.data.value;

      const subscriptionsPromises = topics.map((topic) =>
        getTopicSubscriptions(
          authInfo.accessToken,
          authInfo.fingerPrint,
          topic.id
        )
      );
      const subscriptionsResponses = await Promise.all(subscriptionsPromises);
      const allSubscriptions = subscriptionsResponses.flatMap(
        (response) => response.value
      );

      const matchingTopic = topics.find((topic) =>
        allSubscriptions.some((sub) => sub.userId === organizationOwnerId)
      );

      if (matchingTopic) {
        navigate(`/chats/${matchingTopic.id}`);
      } else {
        const newTopicResponse = await createNewTopic(
          authInfo.accessToken,
          authInfo.fingerPrint,
          authInfo.userId,
          organizationOwnerId,
          organizationName
        );
        navigate(`/chats/${newTopicResponse.data.id}`);
      }
    } catch (e) {
      if (isError(e)) {
        console.error(e.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <HeadBlock />
      <TopNavPortal title="仕事詳細" onGoBack={handleGoBack} />
      {product && (
        <Suspense fallback={<Loading />}>
          <ProductDetailInfo product={product} />
        </Suspense>
      )}
      <BottomAppBar
        minHeight="120px"
        children={
          <Stack spacing={2}>
            <LoadingButton
              variant="outlined"
              color="primary"
              disabled={!canOrderCancel(order)}
              loading={isLoading}
              fullWidth
              onClick={() => setIsCancelModal(true)}
              sx={{ flex: '1 1 0' }}
            >
              {order?.status === OrderStatus.PENDING
                ? '応募を辞退'
                : '勤務予定の辞退'}
            </LoadingButton>
            <LoadingButton
              variant="outlined"
              color="primary"
              loading={isLoading}
              fullWidth
              onClick={handleClickOpenChat}
              sx={{ flex: '1 1 0' }}
            >
              メッセージを送る
            </LoadingButton>
          </Stack>
        }
      />
      <CancelModal
        order={order}
        open={isCancelModal}
        onClose={() => setIsCancelModal(false)}
        onSubmit={updateOrderToCancel}
      />
    </>
  );
}
